import { StringUtils } from '@/common/utils/stringUtils';

export const CONFIG = {
    SECRET_KEY: process.env.VUE_APP_CRYPTO_SECRET_KEY, // must be 32 chars for 256-bit key
    ALGORITHM: 'AES-GCM',
    IV_LENGTH: 12
};

let cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
    if (!cachedKey) {
        const keyData = new TextEncoder().encode(CONFIG.SECRET_KEY);
        cachedKey = await crypto.subtle.importKey('raw', keyData, { name: CONFIG.ALGORITHM }, false, [
            'encrypt',
            'decrypt'
        ]);
    }

    return cachedKey;
}

function isValidEncryptedFormat(text: string): boolean {
    const base64Regex = /^[A-Za-z0-9+/=]+:[A-Za-z0-9+/=]+$/;
    return base64Regex.test(text);
}

export async function encrypt(text: string): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(CONFIG.IV_LENGTH));
    const key = await getKey();
    const encoded = new TextEncoder().encode(text);
    const encrypted = await crypto.subtle.encrypt({ name: CONFIG.ALGORITHM, iv }, key, encoded);
    const ivBase64 = btoa(String.fromCharCode.apply(null, Array.from(iv)));
    const encryptedBase64 = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(encrypted))));
    return `${ivBase64}:${encryptedBase64}`;
}

export async function decrypt(encryptedText: string): Promise<string> {
    if (!isValidEncryptedFormat(encryptedText)) {
        return StringUtils.Characters.Empty;
    }

    const [ivBase64, encryptedBase64] = encryptedText?.split(':');
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
    const data = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
    const key = await getKey();
    const decrypted = await crypto.subtle.decrypt({ name: CONFIG.ALGORITHM, iv }, key, data);

    return new TextDecoder().decode(decrypted);
}
